import { groupBy } from 'es-toolkit';
import React, { useMemo, useState } from 'react';
import { Typography } from '@mui/material';

import { closeCurrentPopup, Dialog } from '@paragon/popup-state';
import { ProductionGroupSelection } from '@paragon/production-app-utilities';
import { notNullish } from '@paragon/utilities';

import { updateAnalysisSetGuids } from '../../store/actions/productionComponentInstances';
import {
  loadProductionGroupData,
  loadProductionGroupDataForOrphanedInstances,
} from '../../store/actions/productionGroups';
import { getScopeComponentDesignGuids, getScopeProjectGuids, ProjectActionScope } from '../../store/projectActionScope';
import { getAnalysisSetGuidsByProductionGroupGuid } from '../../store/selectors/getAnalysisSetGuidsByProductionGroupGuid';
import { getAnalysisSetGuidToComponentDesignName } from '../../store/selectors/getAnalysisSetGuidToComponentDesignName';
import { getComponentDesignGuidToMostRecentAnalysisSetGuidMap } from '../../store/selectors/getComponentDesignGuidToMostRecentAnalysisSetGuidMap';
import { getProductionComponentInstancesByProductionGroupGuid } from '../../store/selectors/getProductionComponentInstancesByProductionGroupGuid';
import { getProductionGroupsByProjectGuid } from '../../store/selectors/getProductionGroupsByProjectGuid';
import { useAppDispatch, useAppSelector } from '../hooks';
import { ReplaceProductionComponentsList } from './ReplaceProductionComponentsList';

export function ReplaceProductionComponentsDialog({ scope }: { readonly scope: ProjectActionScope }) {
  const dispatch = useAppDispatch();
  const productionGroupsByProjectGuid = useAppSelector(getProductionGroupsByProjectGuid);
  const componentDesignGuids = getScopeComponentDesignGuids(scope);
  const componentDesignGuidToMostRecentAnalysisSetGuid = useAppSelector(
    getComponentDesignGuidToMostRecentAnalysisSetGuidMap,
  );
  const checkedAnalysisSetGuids = componentDesignGuids
    .map((componentDesignGuid) => componentDesignGuidToMostRecentAnalysisSetGuid[componentDesignGuid])
    .filter(notNullish);
  const analysisSetGuidsByProductionGroup = useAppSelector(getAnalysisSetGuidsByProductionGroupGuid);
  const productionComponentInstancesByProductionGroupGuid = useAppSelector(
    getProductionComponentInstancesByProductionGroupGuid,
  );
  const analysisSetGuidToComponentDesignName = useAppSelector(getAnalysisSetGuidToComponentDesignName);

  const projectGuid = getScopeProjectGuids(scope)[0];

  const productionGroupsForActiveProject = productionGroupsByProjectGuid[projectGuid];

  const [activeProductionGroupGuid, setActiveProductionGroupGuid] = useState<string | null>(null);

  const productionComponentInstancesForActiveProductionGroupByAnalysisSetGuid = useMemo(() => {
    if (!activeProductionGroupGuid) return {};
    const productionComponentInstancesForActiveProductionGroup =
      productionComponentInstancesByProductionGroupGuid[activeProductionGroupGuid];
    return groupBy(productionComponentInstancesForActiveProductionGroup, (instance) => instance.analysisSetGuid);
  }, [activeProductionGroupGuid, productionComponentInstancesByProductionGroupGuid]);

  const existingTrussesWithSameName = useMemo(() => {
    if (activeProductionGroupGuid === null) return {} as { [newAnalysisSetGuid: string]: readonly string[] };

    const oldAnalysisSetGuidsByName = groupBy(
      analysisSetGuidsByProductionGroup[activeProductionGroupGuid],
      (analysisSetGuid) => analysisSetGuidToComponentDesignName[analysisSetGuid],
    );
    return checkedAnalysisSetGuids.reduce(
      (replaceMap, newAnalysisSetGuid) => {
        const trussName = analysisSetGuidToComponentDesignName[newAnalysisSetGuid];
        const trussNameMatchesButIsOnTheSameAnalysisSet = (oldAnalysisSetGuidsByName[trussName] ?? []).every(
          (oldAnalysisSetGuid) => oldAnalysisSetGuid === newAnalysisSetGuid,
        );
        if (trussNameMatchesButIsOnTheSameAnalysisSet) return replaceMap;
        replaceMap[newAnalysisSetGuid] = oldAnalysisSetGuidsByName[trussName] ?? [];
        return replaceMap;
      },
      {} as { [newAnalysisSetGuid: string]: readonly string[] },
    );
  }, [
    activeProductionGroupGuid,
    analysisSetGuidsByProductionGroup,
    checkedAnalysisSetGuids,
    analysisSetGuidToComponentDesignName,
  ]);

  async function updateActiveProductionGroupGuid(productionGroupGuid: string | null) {
    // When selecting a production group, load its production component instances so that we can compare them against
    // the trusses that are being added.
    if (productionGroupGuid !== null) {
      await dispatch(loadProductionGroupData(productionGroupGuid));

      // Load histories and summaries for any components in the production group that are not in the project. This
      // supports cases where the instance references a component that is not in the project anymore because it was
      // deleted or converted to settings. In these cases, we still want to do name matching to replace the components.
      // In order to do that name matching, we need to load the histories and summaries.
      await dispatch(loadProductionGroupDataForOrphanedInstances(productionGroupGuid));
    }

    setActiveProductionGroupGuid(productionGroupGuid);
  }

  return (
    <Dialog
      title="Replace components"
      open
      submitText="Replace"
      onSubmit={() => {
        const productionComponentInstanceGuidToUpdatedAnalysisSetGuid = Object.keys(existingTrussesWithSameName).reduce(
          (updateMap, checkedNewAnalysisSetGuid) => {
            const oldAnalysisSetGuids = existingTrussesWithSameName[checkedNewAnalysisSetGuid];
            for (const oldAnalysisSetGuid of oldAnalysisSetGuids) {
              const affectedInstances =
                productionComponentInstancesForActiveProductionGroupByAnalysisSetGuid[oldAnalysisSetGuid];
              for (const affectedInstance of affectedInstances) {
                updateMap[affectedInstance.guid] = checkedNewAnalysisSetGuid;
              }
            }
            return updateMap;
          },
          {} as { [productionComponentInstanceGuid: string]: string },
        );
        dispatch(updateAnalysisSetGuids(productionComponentInstanceGuidToUpdatedAnalysisSetGuid));
        dispatch(closeCurrentPopup());
      }}
      onCancel={() => void dispatch(closeCurrentPopup())}
      contentSx={{ width: '800px', height: '600px' }}
      submitDisabled={activeProductionGroupGuid === null || Object.keys(existingTrussesWithSameName).length === 0}
      enterKeySubmits
    >
      <Typography sx={{ mb: 2 }}>
        This action will replace all instances of the matched name, irrespective of the quantity of the new component.
        The quantities as they are in the batches and bundles will be preserved. Positions within the bundle will be
        preserved, but if spans have changed there may now be overlap in the bundle. After performing this action check
        the bundles for warnings.
      </Typography>
      <Typography sx={{ mb: 2 }}>
        Trusses without a name match in the production group or that already match the current version are omitted from
        this list.
      </Typography>
      <ProductionGroupSelection
        activeProductionGroupGuid={activeProductionGroupGuid}
        setActiveProductionGroupGuid={updateActiveProductionGroupGuid}
        productionGroups={productionGroupsForActiveProject}
      />
      {activeProductionGroupGuid && (
        <ReplaceProductionComponentsList
          existingTrussesWithSameName={existingTrussesWithSameName}
          productionComponentInstancesForActiveProductionGroupByAnalysisSetGuid={
            productionComponentInstancesForActiveProductionGroupByAnalysisSetGuid
          }
        />
      )}
    </Dialog>
  );
}
